import { useState } from 'react';
import { Link, Navigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useAuth } from '../hooks/useAuth';
import { useReunion } from '../contexts/ReunionContext';
import { Button } from '../components/Button';
import { ApiError } from '../api/client';
import { api } from '../api/client';

// ============ VALIDATION SCHEMA ============
const loginSchema = z.object({
  email: z.string().email('Valid email required'),
  password: z.string().min(1, 'Password is required'),
});

type LoginFormData = z.infer<typeof loginSchema>;

// ============ STYLE CONSTANTS ============
const inputClasses =
  'w-full bg-slate-50 border border-slate-200 rounded-lg p-3 text-slate-700 placeholder:text-slate-400 focus:ring-2 focus:ring-primary focus:border-primary transition-all outline-none';

const labelClasses = 'block text-sm font-bold text-slate-700 mb-1.5';

const errorClasses = 'text-xs text-error mt-1';

// ============ COMPONENT ============
const Login = () => {
  const { currentUser, login } = useAuth();
  const { activeReunionId } = useReunion();
  const [serverError, setServerError] = useState<string | null>(null);
  const [needsVerification, setNeedsVerification] = useState(false);
  const [resendingVerification, setResendingVerification] = useState(false);
  const [resendSuccess, setResendSuccess] = useState(false);

  const {
    register,
    handleSubmit,
    getValues,
    formState: { errors, isSubmitting },
  } = useForm<LoginFormData>({
    resolver: zodResolver(loginSchema),
  });

  // Redirect if already authenticated (must be after all hooks)
  if (currentUser) return <Navigate to="/dashboard" replace />;

  const onSubmit = async (data: LoginFormData) => {
    setServerError(null);
    setNeedsVerification(false);
    setResendSuccess(false);
    try {
      await login({ email: data.email, password: data.password, reunionId: activeReunionId ?? undefined });
    } catch (err: unknown) {
      if (err instanceof ApiError && err.code === 'EMAIL_NOT_VERIFIED') {
        setNeedsVerification(true);
      } else {
        const message = err instanceof Error ? err.message : 'Login failed. Please check your credentials.';
        setServerError(message);
      }
    }
  };

  const handleResendVerification = async () => {
    setResendingVerification(true);
    setResendSuccess(false);
    try {
      await api.post('/auth/resend-verification', { email: getValues('email') });
      setResendSuccess(true);
    } catch {
      // Still show success to avoid email enumeration
      setResendSuccess(true);
    } finally {
      setResendingVerification(false);
    }
  };

  return (
    <div className="bg-background">
      {/* ===== SECTION 1: PAGE HERO ===== */}
      <section className="relative py-24 lg:py-32 bg-slate-900 overflow-hidden">
        <div className="absolute inset-0 bg-cover bg-center bg-no-repeat opacity-15" style={{ backgroundImage: "url('/images/goldson-wade-2018-hero.jpg')" }} />
        <div className="absolute inset-0 bg-gradient-to-br from-slate-900 via-slate-800/90 to-primary-dark/85" />
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-primary/20 border border-primary/30 mb-6">
            <span className="w-2 h-2 rounded-full bg-primary animate-pulse" />
            <span className="text-xs font-bold tracking-widest uppercase text-white">Welcome Back</span>
          </span>
          <h1 className="heading-hero text-white mb-6 max-w-4xl mx-auto">Sign In to Your Account</h1>
          <p className="text-lg text-slate-200 leading-relaxed max-w-2xl mx-auto">
            Log in to access your reunion dashboard, manage your registration,
            and stay connected with your family.
          </p>
        </div>
      </section>

      {/* ===== SECTION 2: LOGIN FORM ===== */}
      <section className="py-24 bg-white">
        <div className="max-w-md mx-auto px-4 sm:px-6 lg:px-8">
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-5" noValidate>
            {/* Server Error */}
            {serverError && (
              <div className="rounded-lg bg-error/10 border border-error/20 p-4 text-sm text-error">
                {serverError}
              </div>
            )}

            {/* Email Not Verified */}
            {needsVerification && (
              <div className="rounded-lg bg-amber-50 border border-amber-200 p-5">
                <p className="text-sm font-bold text-amber-800 mb-2">Email not verified yet</p>
                <p className="text-sm text-amber-700 mb-3">
                  Before you can log in, you need to click the verification link we sent to your email.
                  Check your inbox (and spam/junk folder) for an email from <strong>Goldson-Wade Reunion</strong>.
                </p>
                {resendSuccess ? (
                  <p className="text-sm text-green-700 font-medium">
                    A new verification email has been sent. Check your inbox.
                  </p>
                ) : (
                  <button
                    type="button"
                    onClick={handleResendVerification}
                    disabled={resendingVerification}
                    className="text-sm font-bold text-primary hover:underline disabled:opacity-50"
                  >
                    {resendingVerification ? 'Sending...' : "Didn't get it? Resend verification email"}
                  </button>
                )}
              </div>
            )}

            {/* Email */}
            <div>
              <label htmlFor="login-email" className={labelClasses}>Email</label>
              <input
                id="login-email"
                type="email"
                placeholder="you@example.com"
                className={inputClasses}
                {...register('email')}
              />
              {errors.email && <p className={errorClasses}>{errors.email.message}</p>}
            </div>

            {/* Password */}
            <div>
              <div className="flex items-center justify-between mb-1.5">
                <label htmlFor="login-password" className="text-sm font-bold text-slate-700">Password</label>
                <Link
                  to="/forgot-password"
                  className="text-xs text-primary font-semibold hover:underline"
                >
                  Forgot Password?
                </Link>
              </div>
              <input
                id="login-password"
                type="password"
                placeholder="Enter your password"
                className={inputClasses}
                {...register('password')}
              />
              {errors.password && <p className={errorClasses}>{errors.password.message}</p>}
            </div>

            {/* Submit */}
            <Button
              type="submit"
              variant="primary"
              size="lg"
              className="w-full"
              disabled={isSubmitting}
            >
              {isSubmitting ? 'Signing In...' : 'Sign In'}
              {!isSubmitting && (
                <svg className="w-4 h-4 ml-2" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M14 5l7 7m0 0l-7 7m7-7H3" />
                </svg>
              )}
            </Button>

            {/* Register Link */}
            <p className="text-center text-sm text-slate-600">
              Don't have an account?{' '}
              <Link to="/register" className="text-primary font-bold hover:underline">
                Sign up
              </Link>
            </p>
          </form>
        </div>
      </section>
    </div>
  );
};

export default Login;
